// ─────────────────────────────────────────────────────────────────────────────
//  Shared primitive UI components for the admin panel
//  Import individually: import { Button, Input, Badge, FIELD_CLS } from '@/components/ui/primitives'
// ─────────────────────────────────────────────────────────────────────────────
import { Search, Inbox } from 'lucide-react'

// ── Shared field class string ─────────────────────────────────────────────────
// Use this on raw <input>, <select>, <textarea> elements for consistent styling.
// The `.field-base` CSS class is defined in index.css with full dark mode support.
export const FIELD_CLS      = 'field-base'
export const FIELD_AUTO_CLS = 'field-base field-auto flex-shrink-0'
export const FIELD_ERR_CLS  = 'field-base field-error'

// ── Label ─────────────────────────────────────────────────────────────────────
export function FieldLabel({ children, required }) {
  return (
    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  )
}

// ── SearchInput ───────────────────────────────────────────────────────────────
// Consistent search input with Lucide Search icon — use instead of raw <input>+emoji
export function SearchInput({ value, onChange, placeholder = 'Qidirish...', className = '' }) {
  return (
    <div className={`relative flex-1 min-w-0 ${className}`}>
      <Search
        size={15}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 pointer-events-none"
      />
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="field-base"
        style={{ paddingLeft: '2.25rem' }}
      />
    </div>
  )
}

// ── Button ────────────────────────────────────────────────────────────────────
const BUTTON_VARIANTS = {
  primary:   'bg-[var(--coral)] text-white hover:opacity-90 active:opacity-80',
  secondary: 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700',
  danger:    'bg-red-500 text-white hover:bg-red-600 active:bg-red-700',
  ghost:     'text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800',
  success:   'bg-[var(--mint)] text-white hover:opacity-90',
}
const BUTTON_SIZES = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-base',
}

export function Button({
  children, variant = 'primary', size = 'md',
  className = '', disabled = false, loading = false,
  type = 'button', onClick, icon,
}) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={[
        'inline-flex items-center justify-center gap-2 font-medium rounded-lg',
        'transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      ].join(' ')}
    >
      {loading ? <Spinner size={size === 'lg' ? 'md' : 'sm'} /> : icon}
      {children}
    </button>
  )
}

// ── Input ─────────────────────────────────────────────────────────────────────
export function Input({ label, hint, error, className = '', ...props }) {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {label}
          {props.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <input
        {...props}
        className={[
          FIELD_CLS,
          error ? 'field-error' : '',
          className,
        ].join(' ')}
      />
      {hint && !error && <p className="text-xs text-gray-500 dark:text-slate-400">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

// ── Textarea ──────────────────────────────────────────────────────────────────
export function Textarea({ label, hint, error, rows = 3, className = '', ...props }) {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {label}
          {props.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <textarea
        rows={rows}
        {...props}
        className={[
          FIELD_CLS,
          error ? 'field-error' : '',
          className,
        ].join(' ')}
      />
      {hint && !error && <p className="text-xs text-gray-500 dark:text-slate-400">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

// ── Select ────────────────────────────────────────────────────────────────────
export function Select({ label, hint, error, children, className = '', ...props }) {
  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          {label}
          {props.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <select
        {...props}
        className={[
          FIELD_CLS,
          error ? 'field-error' : '',
          className,
        ].join(' ')}
      >
        {children}
      </select>
      {hint && !error && <p className="text-xs text-gray-500 dark:text-slate-400">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

// ── Badge ─────────────────────────────────────────────────────────────────────
const BADGE_VARIANTS = {
  blue:    'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  green:   'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  amber:   'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  red:     'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  gray:    'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
  purple:  'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
}

export function Badge({ children, variant = 'gray', className = '' }) {
  return (
    <span className={[
      'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
      BADGE_VARIANTS[variant] ?? BADGE_VARIANTS.gray,
      className,
    ].join(' ')}>
      {children}
    </span>
  )
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, className = '', padding = true }) {
  return (
    <div className={[
      'bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm',
      padding ? 'p-6' : '',
      className,
    ].join(' ')}>
      {children}
    </div>
  )
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ size = 'md', className = '' }) {
  const sz = { sm: 'w-3.5 h-3.5', md: 'w-5 h-5', lg: 'w-7 h-7' }[size] ?? 'w-5 h-5'
  return (
    <div
      className={[sz, 'border-2 border-current border-t-transparent rounded-full animate-spin', className].join(' ')}
      role="status" aria-label="Loading"
    />
  )
}

// ── PageHeader ────────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  )
}

// ── StatCard ──────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, color = 'blue', delta }) {
  const colors = {
    blue:   { bg: 'bg-blue-50 dark:bg-blue-900/20',    icon: 'bg-blue-100 dark:bg-blue-800/50 text-blue-600 dark:text-blue-400'    },
    green:  { bg: 'bg-emerald-50 dark:bg-emerald-900/20', icon: 'bg-emerald-100 dark:bg-emerald-800/50 text-emerald-600 dark:text-emerald-400'},
    amber:  { bg: 'bg-amber-50 dark:bg-amber-900/20',   icon: 'bg-amber-100 dark:bg-amber-800/50 text-amber-600 dark:text-amber-400'  },
    purple: { bg: 'bg-purple-50 dark:bg-purple-900/20', icon: 'bg-purple-100 dark:bg-purple-800/50 text-purple-600 dark:text-purple-400'},
  }[color] ?? { bg: 'bg-gray-50 dark:bg-slate-800', icon: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300' }

  return (
    <div className={`rounded-xl p-5 border border-gray-100 dark:border-slate-700 ${colors.bg}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value ?? '—'}</p>
          {delta !== undefined && (
            <p className={`text-xs mt-1 font-medium ${delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
              {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}%
            </p>
          )}
        </div>
        {icon && (
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl ${colors.icon}`}>
            {icon}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Toggle (checkbox-style) ───────────────────────────────────────────────────
export function Toggle({ label, checked, onChange, hint }) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={[
          'relative flex-none w-10 h-6 rounded-full transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-slate-600',
        ].join(' ')}
      >
        <span className={[
          'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
          checked ? 'translate-x-4' : 'translate-x-0',
        ].join(' ')} />
      </button>
      <div>
        {label && <p className="text-sm font-medium text-gray-700 dark:text-slate-300">{label}</p>}
        {hint  && <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{hint}</p>}
      </div>
    </div>
  )
}

// ── EmptyState ────────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  const defaultIcon = <Inbox size={48} className="text-gray-300 dark:text-slate-600" />
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-4 flex items-center justify-center text-gray-300 dark:text-slate-600">
        {icon ?? defaultIcon}
      </div>
      <h3 className="font-semibold text-gray-700 dark:text-slate-300 mb-1">{title}</h3>
      {description && <p className="text-sm text-gray-500 dark:text-slate-400 max-w-xs">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

// ── SortTh ────────────────────────────────────────────────────────────────────
export function SortTh({ sortKey, currentKey, currentDir, onSort, children, className = '' }) {
  const active = currentKey === sortKey
  return (
    <th
      className={`text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide cursor-pointer select-none hover:text-gray-700 dark:hover:text-slate-200 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <span className="text-[10px]">
          {active ? (currentDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </span>
    </th>
  )
}

// ── TableFooter ───────────────────────────────────────────────────────────────
export function TableFooter({ page, totalPages, pageSize, totalCount, onPageChange, onPageSizeChange }) {
  const total = totalPages || 1

  // Build compact page list: always show 1, last, and ±1 around current
  const pages = []
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || Math.abs(i - page) <= 1) pages.push(i)
    else if (pages[pages.length - 1] !== '...') pages.push('...')
  }

  const NavBtn = ({ disabled, onClick, children }) => (
    <button
      disabled={disabled}
      onClick={onClick}
      className="w-7 h-7 rounded-lg flex items-center justify-center text-[14px] transition-colors disabled:opacity-30"
      style={{ color: 'var(--ink-3)' }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--bg-soft)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
    >{children}</button>
  )

  return (
    <div
      className="flex items-center justify-between px-4 py-2.5 border-t"
      style={{ borderColor: 'var(--border)' }}
    >
      {/* Left: per-page selector + total count */}
      <div className="flex items-center gap-3 text-[12px]" style={{ color: 'var(--ink-3)' }}>
        {pageSize != null && onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <span>Sahifada:</span>
            <select
              value={pageSize}
              onChange={e => { onPageSizeChange(Number(e.target.value)); onPageChange(1) }}
              className="rounded-lg border px-2 py-1 text-[12px] outline-none"
              style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink-2)' }}
            >
              {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        )}
        {totalCount != null && (
          <span>Jami: <span style={{ color: 'var(--ink-2)', fontWeight: 600 }}>{totalCount}</span></span>
        )}
      </div>

      {/* Right: page number buttons */}
      {total > 1 && (
        <div className="flex items-center gap-0.5">
          <NavBtn disabled={page <= 1} onClick={() => onPageChange(page - 1)}>‹</NavBtn>
          {pages.map((p, idx) =>
            p === '...' ? (
              // Keyed by position, not `p` — plain array-index keys collide
              // with the numbered-button keys below (both draw from the same
              // small integer range), which is what triggered React's
              // "two children with the same key" warning.
              <span key={`ellipsis-${idx}`} className="w-7 h-7 flex items-center justify-center text-[12px]"
                style={{ color: 'var(--ink-3)' }}>…</span>
            ) : (
              <button
                key={`page-${p}`}
                onClick={() => onPageChange(p)}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-[12px] font-medium transition-colors"
                style={p === page
                  ? { background: 'var(--coral)', color: '#fff' }
                  : { color: 'var(--ink-2)' }
                }
                onMouseEnter={e => { if (p !== page) e.currentTarget.style.background = 'var(--bg-soft)' }}
                onMouseLeave={e => { if (p !== page) e.currentTarget.style.background = 'transparent' }}
              >{p}</button>
            )
          )}
          <NavBtn disabled={page >= total} onClick={() => onPageChange(page + 1)}>›</NavBtn>
        </div>
      )}
    </div>
  )
}
